import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import { normalizeTournaments } from '@/utils/normalizeTournaments';
import type { PaginatedMeta, Tournament, TournamentListParams, TournamentListSource } from '@/types';

export interface TournamentListPage {
  items: Tournament[];
  meta: PaginatedMeta;
}

/**
 * Query key prefixes shared with the legacy list queries, so every existing
 * invalidation (`['my-tournaments']`, `['public-tournaments']`,
 * `['archived-tournaments']`) also refreshes the paginated lists.
 */
export const SOURCE_KEY: Record<TournamentListSource, string> = {
  my: 'my-tournaments',
  public: 'public-tournaments',
  archived: 'archived-tournaments',
};

type ListFetcher = (params: TournamentListParams, signal?: AbortSignal) => Promise<any>;

const FETCHERS: Record<TournamentListSource, ListFetcher> = {
  my: (params, signal) => apiClient.getTournaments(params, signal),
  public: (params, signal) => apiClient.getPublicTournaments(params, signal),
  archived: (params, signal) => apiClient.getArchivedTournaments(params, signal),
};

/**
 * One page of a tournament list endpoint, normalized. `signal` (React Query's)
 * cancels the request once nobody needs its answer.
 */
export async function fetchTournamentPage(
  source: TournamentListSource,
  params: TournamentListParams,
  signal?: AbortSignal,
): Promise<TournamentListPage> {
  const response = await FETCHERS[source](params, signal);
  const body = response.data;
  return { items: normalizeTournaments(body.data), meta: body.meta };
}

/**
 * Query key base of a list and whether it can be fetched: public lists are
 * user-independent; "my" and "archived" are per user and wait for the user id.
 * Every query of a list builds its key on this base, so list invalidations
 * reach it too.
 */
export function useListScope(source: TournamentListSource): { keyBase: unknown[]; ready: boolean } {
  const { user } = useAuth();
  if (source === 'public') return { keyBase: [SOURCE_KEY.public], ready: true };
  return { keyBase: [SOURCE_KEY[source], user?.id], ready: !!user?.id };
}

/**
 * One page of a tournament list (1-based `page` + `limit`). Filter and page
 * changes keep the previous page on screen until the new one lands.
 */
export function useTournamentPagedList(
  source: TournamentListSource,
  params: Omit<TournamentListParams, 'offset'>,
) {
  const { keyBase, ready } = useListScope(source);

  const query = useQuery({
    queryKey: [...keyBase, 'paged', params],
    queryFn: () => fetchTournamentPage(source, params),
    placeholderData: keepPreviousData,
    enabled: ready,
  });

  return { ...query, items: query.data?.items ?? [], meta: query.data?.meta };
}
